import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Scrollbar from '../scrollbar';
import { useUserDetails } from '../../hooks/useUserDetails';

const PICKEM_STATUS_COLORS = {
  'DRAFT': 'error',
  'UPCOMING': 'info',
  'COMPLETE': 'success',
  'IN_PLAY': 'primary',
};

const PICKEM_STATUS = {
  'DRAFT': 'Draft',
  'UPCOMING': 'Upcoming',
  'COMPLETE': 'Complete',
  'IN_PLAY': 'In Play',
};

export default function EntriesTab({ userId }) {
  const navigate = useNavigate();
  const { entries, totalEntries, fetchUserEntries } = useUserDetails();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      await fetchUserEntries(userId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
    } catch (err) {
      console.error('Failed to load entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewSelections = (pickemId) => {
    navigate(`/dashboard/pickems/${pickemId}/user-selections/${userId}`);
  };

  if (loading && entries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography variant="body2" color="text.secondary">
          No entries found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contest ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Points</TableCell>
                <TableCell align="center">Rank</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.pickem_id} hover>
                  <TableCell>{entry.pickem_id}</TableCell>
                  <TableCell>{entry.title_en}</TableCell>
                  <TableCell>
                    {entry.start_utc ? new Date(entry.start_utc).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                        <Chip
                          label={PICKEM_STATUS[entry.status] || 'Unknown'}
                          size="small"
                          color={PICKEM_STATUS_COLORS[entry.status] || 'default'}
                        />
                      </TableCell>
                  <TableCell align="center">{entry.points || 0}</TableCell>
                  <TableCell align="center">{entry.rank || '-'}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewSelections(entry.pickem_id)}
                    >
                      View Selections
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalEntries}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
